import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
// Import Types
import type { UseFilterTagsReturnValue } from './useFilterTags';
// Import Components
import SingleInputModal from 'components/common/Modals/SingleInputModal';
import ConfigureFilterTagModal from './ConfigureFilterTagModal';
// Import Services
import { isAlreadyExistFiltersTagName } from './service';
// Import Constants
import { l } from 'constants/common';

interface Props {
    filtersTagKey: string;
    filterTags: UseFilterTagsReturnValue;
}

/**
 * Renders the "Save filters" and "Saved filters" modals driven by `useFilterTags` state.
 */
const FilterTagModals: FC<Props> = ({ filtersTagKey, filterTags }) => {
    const { t } = useTranslation();
    const {
        filtersTagsList,
        isFiltersTagListLoading,
        isAddFilterLoading,
        isVisibleSaveFilterTagModal,
        isVisibleConfigureFilterTagModal,
        filterName,
        setFilterName,
        getFiltersTagsList,
        applyFiltersToFilterHolderAndFilterTag,
        handleSaveModalCancel,
        handleConfigureModalCancel,
        handleSaveFilter,
    } = filterTags;

    return (
        <>
            {isVisibleSaveFilterTagModal && (
                <SingleInputModal
                    titleTextLabel={l.SaveFilters}
                    isModalVisible={isVisibleSaveFilterTagModal}
                    onCancel={handleSaveModalCancel}
                    onOk={handleSaveFilter}
                    inputName={filterName ?? ''}
                    setInputName={setFilterName}
                    inputPlaceholderLabel={l.FilterName}
                    inputNoteLabel={l.WriteANameForTheFilter}
                    isLoading={isAddFilterLoading}
                    externalValidationFn={(value) => !isAlreadyExistFiltersTagName(value, filtersTagsList)}
                    externalValidationErrorMessage={t(l.FilterGroupNameAlreadyExistInFiltersGroupList, {
                        filterGroupName: filterName,
                    })}
                />
            )}

            {isVisibleConfigureFilterTagModal && (
                <ConfigureFilterTagModal
                    filtersTagKey={filtersTagKey}
                    isModalVisible={isVisibleConfigureFilterTagModal}
                    data={filtersTagsList}
                    isDataLoading={isFiltersTagListLoading}
                    onCancel={handleConfigureModalCancel}
                    getFiltersTagsList={getFiltersTagsList}
                    applyFiltersAction={applyFiltersToFilterHolderAndFilterTag}
                />
            )}
        </>
    );
};

export default FilterTagModals;
